import { CalendarEvent, Notification as NotificationModel } from '../models'
import {
  FunnelWebsiteRouteNames,
  calendarRouteNames,
  membershipRouteNames,
  opportunitiesRouteNames,
  tasksRouteNames,
} from '../routes/constants/location'
import NotificationAPIModel, { CustomNotificationType, NotificationType } from '../models/api/notification'
import { acceptIncomingCallAction, declineIncomingCallAction } from '../pmd/components/power_dialer/callActions'

import { OauthService } from '@/services'
import { RouterRegistry } from './routerRegistry'
import axios from 'axios'
import config from '../config'
import firebase from 'firebase/compat/app'
import { formatPhoneNumberE164 } from './helper'
import { i18n } from '@/localization'
import moment from 'moment-timezone'
import router from '../routes'
import { snapshotsRoutes } from '../routes/constants/agency/'
import store from '@/store'

export default class NotificationHelper {
  public static init(registration: ServiceWorkerRegistration) {
    if ('serviceWorker' in navigator) {
      const messaging = firebase.messaging()
      messaging.onMessage(message => NotificationHelper.handleMessage(message, registration))
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data.customPushMessage) {
          NotificationHelper.handleBackgroundClick(event.data.customPushMessage, event.data.notificationAction)
        }
      })
    }
    this.requestPermission()
  }

  public static async requestPermission() {
    if (typeof window !== 'undefined' && (window as any).isElectron) {
      try {
        const auth = await store.dispatch('auth/get')
        if (auth.userId) {
          const token = await (window as any).electronAPI.getFCMToken()
          if (token) {
            await axios.post(`${config.newBaseUrl}/api/token/update`, { token })
          }
        }
      } catch (err) {
        console.log(err)
      }
      return
    }

    if ('serviceWorker' in navigator) {
      try {
        const messaging = firebase.messaging()
        const registration = await navigator.serviceWorker.getRegistration()
        const token = await messaging.getToken({
          vapidKey: config.fcmPublicKey,
          serviceWorkerRegistration: registration,
        })
        const apiKey = store?.state?.auth?.user?.apiKey
        if (token && apiKey) {
          const userId = store?.state?.auth?.user?.userId
          await OauthService.updateSessionToken(apiKey, userId, token)
        }
      } catch (err) {
        console.log(err)
      }
    }
  }

  public static requestBrowserPermission() {
    Notification.requestPermission()
  }

  public static async handleMessage(message: { [key: string]: any }, registration: ServiceWorkerRegistration) {
    const router = RouterRegistry.getRouter()
    const data = message.data
    let title: string = ''
    let notification: Notification
    switch (data.type) {
      case 'sms':
        title = data.title ?? 'New message from ' + (data.fromName || data.fromNumber)
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = () => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'email':
        title = data.title ?? 'New message from ' + (data.fromName || data.fromEmail)
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = () => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'fb_messenger':
      case 'gmb':
        title = data.title ?? 'New message from ' + (data.fromName || 'unknown')
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = () => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'call':
        title = data.title ?? `Incoming call from ${data.name || 'unknown'}`
        let formattedNumber: string
        try {
          formattedNumber = formatPhoneNumberE164(data.fromNumber)
        } catch {
          formattedNumber = 'Unknown Number'
        }
        registration.showNotification(title, {
          body: `${formattedNumber} \n ${data.locationName}`,
          icon: data.logo,
          actions: [
            {
              action: 'accept',
              title: i18n.global.t('common.accept'),
            },
            {
              action: 'decline',
              title: i18n.global.t('common.decline'),
            },
          ],
          data,
        })
        break
      case 'review':
      case 'reviewReply':
        if (data.type === 'review') {
          title = data.title ?? 'New review from ' + data.reviewerName
        } else if (data.type === 'reviewReply') {
          title = data.title ?? data.reviewerName + ' replied to the review'
        }
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = () => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'appointment':
        title = data.title ?? 'New appointment booked by' + data.name
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = () => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'snapshot': {
        title =
          data.title ??
          `${data.snapshotName} ${data.loadStatus && data.loadStatus === 'success' ? 'Success' : 'Failed'}!`
        notification = new Notification(title, {
          body:
            data.body ?? `Snapshot ${data.loadStatus && data.loadStatus === 'success' ? 'load completed' : 'failed'}!`,
        })
        notification.onclick = () => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      }
      case 'voicemail':
        title = data.title ?? 'New voicemail from ' + (data.fromName || data.fromNumber)
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = () => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'smartlist':
        //title = 'Smart List Change' + (data.fromName || data.fromNumber)
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo,
        })
        if (data.listId) {
          notification.onclick = () => {
            notification.close()
            window.focus()
            if (
              router &&
              router.currentRoute.value.params &&
              router.currentRoute.value.params.smart_list === data.listId
            )
              router.go(0)
            else if (router) router.push({ name: 'smart_list', params: { smart_list: data.listId } })
          }
        }
        break
      case NotificationType.NOTIFICATION_TYPE_LISTINGS:
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = () => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case NotificationType.NOTIFICATION_TYPE_LISTINGS_SCHEDULED_REPORTS:
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = () => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'custom':
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'reputation_datashake':
        notification = new Notification(data.title, {
          body: data.message,
          icon: data.logo,
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'custom':
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'wordpress':
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'reputation_gmb_review':
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo,
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
    }
  }

  public static async handleBackgroundClick(data: { [key: string]: any }, notificationAction?: string) {
    const router = RouterRegistry.getRouter()
    NotificationModel.markRead(data.notificationId)
    switch (data.type) {
      case 'fb_messenger':
      case 'email':
      case 'sms':
      case 'gmb':
      case 'instagram':
      case 'voicemail':
      case 'conversation_assigned':
        router.push({
          name: 'contact_detail',
          params: {
            location_id: data.locationId,
            contact_id: data.contactId,
          },
        })
        break
      case 'call': {
        router.push({
          name: 'contact_detail',
          params: {
            location_id: data.locationId,
            contact_id: data.contactId,
          },
        })
        if (notificationAction === 'accept') {
          acceptIncomingCallAction()
        }
        if (notificationAction === 'decline') {
          declineIncomingCallAction()
        }
        break
      }
      case 'task_assigned':
      case 'task_reminder':
        router.push({
          name: tasksRouteNames.default,
          params: {
            location_id: data.locationId,
          },
        })
        break
      case 'review':
      case 'reviewReply':
        router.push({
          name: 'reviews',
          params: { location_id: data.locationId, review_id: data.reviewId },
        })
        break
      case 'appointment':
        const event = await CalendarEvent.getById(data.eventId)
        router.push({
          name: 'calendar-v2',
          params: {
            location_id: data.locationId,
          },
          query: {
            calendar: event.calendarId,
            event_id: data.eventId,
            startDate: moment(event.startTime).valueOf(),
          },
        })
        break
      case 'snapshot':
        {
          router.push({
            name: 'dashboard_default',
            params: {
              location_id: data.locationId,
            },
          })
        }
        break
      case 'custom': {
        switch (data?.actionType) {
          case 'instagram_story':
            {
              router.push({
                name: 'social-planner-dashboard-v2',
                params: {
                  location_id: data.locationId,
                },
              })
            }
            break
          case 'external':
            {
              window.open(data.externalUrl)
            }
            break
          default:
            break
        }
      }
      case NotificationType.NOTIFICATION_TYPE_LISTINGS:
        router.push({
          name: 'reputation-listing-v2',
          params: {
            location_id: data.locationId,
          },
          query: {
            utm_source: data?.utmSource,
            utm_campaign: data?.utmCampaign,
          },
        })
        break
      case NotificationType.NOTIFICATION_TYPE_LISTINGS_SCHEDULED_REPORTS:
        if (data?.notificationData?.externalUrl) {
          window.open(data?.notificationData?.externalUrl)
        }
        break
      case 'reputation_datashake':
        router.push({
          name: 'reputation-settings-v2',
          params: {
            location_id: data.locationId,
          },
          query: {
            tab: 'reputationIntegrations',
          },
        })
        break
      case 'wordpress':
        router.push({
          name: 'wordpress-v2',
          params: {
            location_id: data.locationId,
          },
        })
        break
      case 'reputation_gmb_review':
        router.push({
          name: 'reputation-settings-v2',
          params: {
            location_id: data.locationId,
          },
          query: {
            tab: 'reviewsAI',
          },
        })
        break
      // case 'call':
      // 	store.commit('incomingCall/set', {
      // 		from: data.fromNumber,
      // 		to: data.toNumber,
      // 		newCustomer: data.newCustomer,
      // 		appointmentId: data.appointmentId,
      // 		contactId: data.contactId,
      // 		locationId: data.locationId,
      // 	});
      // 	break;
    }
  }
}

export const handleNotificationRedirection = (notification: any) => {
  const router = RouterRegistry.getRouter()
  switch (notification.type) {
    case NotificationType.NOTIFICATION_TYPE_SMS:
    case NotificationType.NOTIFICATION_TYPE_EMAIL:
    case NotificationType.NOTIFICATION_TYPE_FACEBOOK_MESSENGER:
    case NotificationType.NOTIFICATION_TYPE_VOICEMAIL:
    case NotificationType.NOTIFICATION_TYPE_GMB:
    case NotificationType.NOTIFICATION_TYPE_INSTAGRAM:
    case NotificationType.NOTIFICATION_TYPE_CONVERSATION_ASSIGNED:
    case NotificationType.NOTIFICATION_TYPE_CALL:
    case NotificationType.NOTIFICATION_TYPE_WHATSAPP:
    case NotificationType.NOTIFICATION_TYPE_LIVE_CHAT:
    case NotificationType.NOTIFICATION_TYPE_TIKTOK:
      router.push({
        name: 'contact_detail',
        params: {
          location_id: notification.locationId,
          contact_id: notification.notificationData.contactId,
        },
      })
      break
    case NotificationType.NOTIFICATION_TYPE_REVIEW:
    case NotificationType.NOTIFICATION_TYPE_REVIEW_REPLY:
      if (notification.notificationData.conversationId) {
        router.push({
          name: 'conversations-id-v2',
          params: {
            conversation_id: notification.notificationData.conversationId,
            location_id: notification.locationId,
          },
        })
      } else {
        router.push({
          name: 'reputation-reviews-v2',
          params: {
            location_id: notification.locationId,
            review_id: notification.notificationData.reviewId,
          },
        })
      }
      break
    case NotificationType.NOTIFICATION_TYPE_INTERNAL_COMMENT_MENTION:
      if (notification.notificationData.conversationId) {
        router.push({
          name: 'contact_detail',
          params: {
            location_id: notification.locationId,
            contact_id: notification.notificationData.contactId,
          },
          query: {
            focusMessageId: notification.notificationData.messageId,
          },
        })
      }
      break
    case NotificationType.NOTIFICATION_TYPE_APPOINTMENT:
      router.push({
        name: calendarRouteNames.appointmentList,
        params: {
          location_id: notification.locationId,
        },
      })
      break
    case NotificationType.NOTIFICATION_CUSTOM:
      if (notification.notificationData.type === CustomNotificationType.CONTACT) {
        router.push({
          name: 'contact_detail',
          params: {
            location_id: notification.locationId,
            contact_id: notification.notificationData.contactId,
          },
        })
      } else if (notification.notificationData.type === CustomNotificationType.OPPORTUNITY) {
        router.push({
          name: opportunitiesRouteNames.list,
          params: {
            location_id: notification.locationId,
          },
        })
      } else if (notification.notificationData.type === CustomNotificationType.BULK_ACTIONS) {
        router.push(`/v2/location/${notification.locationId}/contacts/bulk/actions`)
      } else if (notification.notificationData.type === CustomNotificationType.CONVERSATION) {
        router.push({
          name: 'conversations-id-v2',
          params: {
            conversation_id: notification.notificationData.conversationId,
            location_id: notification.locationId,
          },
        })
      } else if (notification.notificationData.type === CustomNotificationType.INSTAGRAM_STORY) {
        router.push({
          name: 'social-planner-dashboard-v2',
          params: {
            location_id: notification.locationId,
          },
        })
      } else if (notification.notificationData.type === CustomNotificationType.EXTERNAL) {
        window.open(notification.notificationData.externalUrl)
      } else if (notification.notificationData.type === CustomNotificationType.MEMBERSHIP) {
        router.push({
          name: membershipRouteNames.courses.membershipProductComments,
          params: {
            location_id: notification.locationId,
            id: notification.notificationData.productId,
          },
        })
      } else if (notification.notificationData.type === CustomNotificationType.CLIENT_PORTAL_BRANDED_APP) {
        router.push({
          name: FunnelWebsiteRouteNames.clientPortal.brandedApp,
          params: {
            location_id: notification.locationId,
          },
        })
      }
      break
    case NotificationType.NOTIFICATION_TYPE_SNAPSHOT:
      router.push({
        name: snapshotsRoutes.history,
        params: {
          snapshotId: notification.notificationData.snapshotId,
        },
      })
      break
    case NotificationType.NOTIFICATION_TYPE_TASK_ASSIGNED:
    case NotificationType.NOTIFICATION_TYPE_TASK_REMINDER:
      router.push({
        name: tasksRouteNames.default,
        params: {
          location_id: notification.locationId,
        },
      })
      break
    case NotificationType.NOTIFICATION_TYPE_DAILY_DUE_TASK_SUMMARY:
      router.push({
        name: 'tasks-v2',
        params: {
          location_id: notification.locationId,
        },
        query: {
          assignedTo: notification.userId,
          completed: 'false',
          sortBy: 'dueDate',
          direction: 'asc',
          dueDate: JSON.stringify({
            gt: notification.notificationData.scheduledTime,
            lte: notification.notificationData.endOfTimezoneDay,
          }),
        },
      })
      break
    case NotificationType.NOTIFICATION_TYPE_DAILY_OVERDUE_TASK_SUMMARY:
      router.push({
        name: 'tasks-v2',
        params: {
          location_id: notification.locationId,
        },
        query: {
          assignedTo: notification.userId,
          completed: 'false',
          sortBy: 'dueDate',
          direction: 'desc',
          dueDate: JSON.stringify({ lte: notification.notificationData.scheduledTime }),
        },
      })
      break
    case NotificationType.NOTIFICATION_TYPE_LISTINGS:
      router.push({
        name: 'reputation-listing-v2',
        params: {
          location_id: notification.locationId,
        },
        query: {
          utm_source: notification?.notificationData?.utmSource,
          utm_campaign: notification?.notificationData?.utmCampaign,
        },
      })
      break
    case NotificationType.NOTIFICATION_TYPE_LISTINGS_SCHEDULED_REPORTS:
      if (notification?.notificationData?.externalUrl) {
        window.open(notification?.notificationData?.externalUrl)
      }
      break
    case NotificationType.NOTIFICATION_TYPE_WORDPRESS:
      router.push({
        name: 'wordpress-v2',
        params: {
          location_id: notification.locationId,
        },
      })
      break
    case NotificationType.NOTIFICATION_TYPE_INTERNAL_CHAT_CONVERSATION:
    case NotificationType.NOTIFICATION_TYPE_INTERNAL_CHAT_MESSAGE:
      router.push({
        name: 'conversations-id-v2',
        params: {
          conversation_id: notification.notificationData?.conversationId,
          location_id: notification?.locationId,
        },
        query: {
          type: '6',
        },
      })
      break
    case NotificationType.NOTIFICATION_TYPE_REPUTATION_DATASHAKE:
      router.push({
        name: 'reputation-settings-v2',
        params: {
          location_id: notification?.locationId,
        },
        query: {
          tab: 'reputationIntegrations',
        },
      })
      break
    case NotificationType.NOTIFICATION_TYPE_CALENDAR_INTEGRATION_DISCONNECTED:
      router.push({
        path: `/v2/location/${notification.locationId}/settings/calendars/connections`,
        replace: true,
      })
      break
    case NotificationType.NOTIFICATION_TYPE_REPUTATION_GMB_REVIEW:
      router.push({
        name: 'reputation-settings-v2',
        params: {
          location_id: notification.locationId,
        },
        query: {
          tab: 'reviewsAI',
        },
      })
      break
  }
}

export const handlePushNotificationClickFromDesktop = (notification: any) => {
  handleNotificationRedirection(notification)
  setTimeout(async () => {
    await NotificationAPIModel.markAsReadByNotificationId(notification.notificationId)
  }, 2000)
}
